import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from './Layout';
import { calculateConstellation, generateUserUuid } from './services/api';
import { getAllConstellations } from './utils/constellationData';
import { Button, Card, GalleryZoomImage, DatePickerPro } from './components';

// 四方神兽分组数据（id 对应 constellations.json 中的实际值）
const DIVINE_BEASTS = [
  { name: '青龙', emoji: '🐉', filter: (c: { id: string }) => ['jiao','kang','di','fang','xin','wei','ji'].includes(c.id) },
  { name: '玄武', emoji: '🐢', filter: (c: { id: string }) => ['dou','niu','nv','xu','wei2','shi','bi'].includes(c.id) },
  { name: '白虎', emoji: '🐯', filter: (c: { id: string }) => ['kui','lou','wei3','mao','bi2','zi','shen'].includes(c.id) },
  { name: '朱雀', emoji: '🦅', filter: (c: { id: string }) => ['jing','gui','liu','xing','zhang','yi','zhen'].includes(c.id) },
];

const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [birthday, setBirthday] = useState('');
  const [birthTime, setBirthTime] = useState('');
  const [isUnknownTime, setIsUnknownTime] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    birthday?: string;
    birthTime?: string;
  }>({});
  
  // 画廊筛选状态
  const [activeBeast, setActiveBeast] = useState<string>('all');

  // 图片灯箱状态 — 记录当前索引，范围由 filteredConstellations 决定
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const closeLightbox = () => setLightboxIndex(null);

  // 获取全部28星宿数据
  const allConstellations = getAllConstellations();

  // 根据筛选获取星宿
  const filteredConstellations = activeBeast === 'all'
    ? allConstellations
    : allConstellations.filter(c => DIVINE_BEASTS.find(b => b.name === activeBeast)?.filter(c));

  // 灯箱图片列表（必须在 filteredConstellations 之后声明）
  const lightboxImages = filteredConstellations.map(c => ({
    src: c.imageUrl || c.sculptureImageUrl || '',
    name: c.name,
  }));

  const lightboxImg = lightboxIndex !== null ? lightboxImages[lightboxIndex] : null;

  const goToPrev = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex(i => i !== null ? (i - 1 + lightboxImages.length) % lightboxImages.length : null);
  };

  const goToNext = () => {
    if (lightboxIndex === null) return;
    setLightboxIndex(i => i !== null ? (i + 1) % lightboxImages.length : null);
  };

  // ESC 关闭灯箱 + 方向键切换
  React.useEffect(() => {
    if (lightboxIndex === null) return;
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrev();
      if (e.key === 'ArrowRight') goToNext();
    };
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [lightboxIndex]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    setFieldErrors({});
    
    let hasError = false;
    const newFieldErrors: { birthday?: string; birthTime?: string } = {};
    
    if (!birthday) {
      newFieldErrors.birthday = '请输入您的生日';
      hasError = true;
    } else {
      const selectedDate = new Date(birthday);
      const today = new Date();
      if (selectedDate > today) {
        newFieldErrors.birthday = '生日不能晚于今天';
        hasError = true;
      }
      
      const year = selectedDate.getFullYear();
      if (year < 1900 || year > 2100) {
        newFieldErrors.birthday = '生日年份需在1900-2100年之间';
        hasError = true;
      }
    }
    
    if (!isUnknownTime) {
      if (!birthTime) {
        newFieldErrors.birthTime = '请输入出生时辰，或选择"不确定时辰"';
        hasError = true;
      } else {
        const timeRegex = /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/;
        if (!timeRegex.test(birthTime)) {
          newFieldErrors.birthTime = '请输入有效的时辰格式 (HH:MM)';
          hasError = true;
        }
      }
    }
    
    if (hasError) {
      setFieldErrors(newFieldErrors);
      setTimeout(() => {
        const firstErrorElement = document.querySelector('.error-field');
        if (firstErrorElement) {
          firstErrorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      return;
    }
    
    // 时辰处理：不确定时辰默认正午12点，23点后按次日查表
    let birthHour: number = 12; // 默认正午
    if (!isUnknownTime && birthTime) {
      const [hours] = birthTime.split(':').map(Number);
      birthHour = hours;
    }
    
    const userUuid = generateUserUuid();
    
    try {
      setIsLoading(true);
      
      const result = await calculateConstellation(birthday, birthHour, userUuid);
      
      if (result.success) {
        // 缓存完整测算结果到 sessionStorage，供结果页使用
        try {
          sessionStorage.setItem('calculationResult', JSON.stringify(result.data));
        } catch (e) {
          // sessionStorage 满了或不可用，不影响主流程
        }
        navigate(`/result/${result.data.constellation.id}`);
      } else {
        setError(result.error || '测算失败，请稍后重试');
      }
    } catch (err) {
      console.error('测算请求失败:', err);
      setError('网络请求失败，请检查网络连接');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Layout>
      <div className="relative">
        {/* 星光背景 */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
          <div className="star-field"></div>
        </div>

        {/* ===== 第一屏：标题 + 引导语 ===== */}
        <section className="relative text-center pt-6 pb-6 md:pt-10 md:pb-10">
          <h1 className="text-3xl md:text-5xl font-bold text-brand-accent mb-4 font-serif-chinese animate-float tracking-wider">
            纸喜爱二十八星宿
          </h1>
          <p className="text-base md:text-lg text-neutral-300 max-w-xl mx-auto leading-relaxed">
            千年演禽法，二十八宿定乾坤。<br className="hidden sm:block" />
            输入生辰，揭晓属于你的本命守护星。
          </p>
        </section>

        {/* ===== 第二屏：星宿测算表单（核心CTA前置） ===== */}
        <section id="calculation-form" className="relative max-w-lg mx-auto mb-16 md:mb-20">
          {/* 表单容器 — 带星轨装饰 */}
          <div className="relative">
            {/* 左上角星轨装饰 */}
            <div className="absolute -top-px -left-px w-20 h-20 overflow-hidden rounded-tl-2xl pointer-events-none" aria-hidden="true">
              <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-brand-accent to-transparent"></div>
              <div className="absolute top-0 left-0 w-px h-full bg-gradient-to-b from-brand-accent to-transparent"></div>
            </div>
            {/* 右下角星轨装饰 */}
            <div className="absolute -bottom-px -right-px w-20 h-20 overflow-hidden rounded-br-2xl pointer-events-none" aria-hidden="true">
              <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-brand-accent to-transparent"></div>
              <div className="absolute bottom-0 right-0 w-px h-full bg-gradient-to-t from-brand-accent to-transparent"></div>
            </div>

            <Card variant="glass" shadow="gold" className="p-6 md:p-8">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-accent mb-6 text-center font-serif-chinese">
                星宿测算
              </h2>
              
              {error && (
                <div className="mb-6 p-4 bg-error/10 border border-error rounded-xl text-error">
                  <div className="flex items-center">
                    <svg className="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    {error}
                  </div>
                </div>
              )}
              
              <form onSubmit={handleSubmit}>
                {/* 生日输入（支持公历/农历/时辰） */}
                <div className="mb-5 error-field">
                  <label className="form-label">出生日期</label>
                  <DatePickerPro
                    value={birthday}
                    onChange={(result) => {
                      setBirthday(result.solarDate);
                      if (!result.hourUnknown) {
                        // 时辰转 HH:00
                        const h = result.hour === 0 ? 23 : result.hour * 2 - 1;
                        setBirthTime(`${String(h).padStart(2, '0')}:00`);
                        setIsUnknownTime(false);
                      } else {
                        setBirthTime('');
                        setIsUnknownTime(true);
                      }
                      if (fieldErrors.birthday) {
                        setFieldErrors({ ...fieldErrors, birthday: undefined });
                      }
                    }}
                  />
                  {fieldErrors.birthday ? (
                    <p className="text-error text-sm mt-2 flex items-center">
                      <svg className="w-4 h-4 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                      {fieldErrors.birthday}
                    </p>
                  ) : (
                    <p className="text-neutral-400 text-sm mt-2">
                      支持 1900-{new Date().getFullYear()} 年 · 可选农历/时辰
                    </p>
                  )}
                </div>
                
                {/* 提交按钮 */}
                <Button
                  type="submit"
                  loading={isLoading}
                  loadingText="星辰推演中..."
                  fullWidth
                  size="lg"
                  className="gold-button py-4 text-lg"
                >
                  测算我的本命星宿
                </Button>
              </form>
            </Card>
          </div>
        </section>

        {/* ===== 第三屏：二十八星宿画廊 ===== */}
        <section id="gallery" className="relative max-w-4xl mx-auto mb-16 md:mb-20">
          <div className="text-center mb-6">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-accent mb-3 font-serif-chinese">
              二十八宿图集
            </h2>
            <p className="text-neutral-300 text-sm md:text-base">
              国漫风格 · 每一尊都是独特的守护
            </p>
          </div>

          {/* 四方神兽筛选标签 */}
          <div className="flex justify-center gap-2 md:gap-3 mb-8 flex-wrap">
            <button
              onClick={() => setActiveBeast('all')}
              className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                activeBeast === 'all'
                  ? 'bg-brand-accent text-brand-primary border-brand-accent shadow-gold-sm'
                  : 'bg-transparent text-neutral-400 border-neutral-700 hover:border-brand-accent/50 hover:text-brand-accent'
              }`}
            >
              全部
            </button>
            {DIVINE_BEASTS.map((beast) => (
              <button
                key={beast.name}
                onClick={() => setActiveBeast(beast.name)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 border ${
                  activeBeast === beast.name
                    ? 'bg-brand-accent text-brand-primary border-brand-accent shadow-gold-sm'
                    : 'bg-transparent text-neutral-400 border-neutral-700 hover:border-brand-accent/50 hover:text-brand-accent'
                }`}
              >
                {beast.emoji} {beast.name}
              </button>
            ))}
          </div>

          {/* 画廊网格 */}
          <Card variant="default" shadow="lg" className="p-4 md:p-6">
            <div className="constellation-gallery" role="list" aria-label="二十八星宿图集">
              {filteredConstellations.map((c) => (
                <div
                  key={c.id}
                  className="constellation-gallery-item"
                  data-name={c.name}
                  role="listitem"
                  aria-label={`${c.name} - ${c.description}，点击查看大图`}
                  tabIndex={0}
                  onClick={() => setLightboxIndex(filteredConstellations.findIndex(fc => fc.id === c.id))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setLightboxIndex(filteredConstellations.findIndex(fc => fc.id === c.id));
                    }
                  }}
                >
                  <img
                    src={c.thumbnailUrl || c.sculptureThumbnailUrl || c.imageUrl || c.sculptureImageUrl || ''}
                    alt={`${c.name}塑像`}
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              ))}
            </div>
          </Card>
        </section>

        {/* ===== 第四屏：核心功能卡片 ===== */}
        <section className="grid md:grid-cols-3 gap-5 md:gap-6 max-w-4xl mx-auto pb-16">
          <Card variant="glass" hoverable shadow="md">
            <div className="w-11 h-11 bg-gradient-to-br from-brand-accent/20 to-brand-accent/40 rounded-lg mb-4 flex items-center justify-center border border-brand-accent/30">
              <span className="text-xl">🐉</span>
            </div>
            <h3 className="text-lg font-bold text-brand-accent mb-2 font-serif-chinese">宿曜经算法</h3>
            <p className="text-neutral-300 text-sm leading-relaxed">
              源自唐代《宿曜经》27宿本命星宿算法，结合28宿值日星宿，推算精准。
            </p>
          </Card>
          <Card variant="glass" hoverable shadow="md">
            <div className="w-11 h-11 bg-gradient-to-br from-brand-accent/20 to-brand-accent/40 rounded-lg mb-4 flex items-center justify-center border border-brand-accent/30">
              <span className="text-xl">🎨</span>
            </div>
            <h3 className="text-lg font-bold text-brand-accent mb-2 font-serif-chinese">新中式美学</h3>
            <p className="text-neutral-300 text-sm leading-relaxed">
              藏青·墨黑·烫金配色，山西泥塑厚重感与国漫线条融合。
            </p>
          </Card>
          <Card variant="glass" hoverable shadow="md">
            <div className="w-11 h-11 bg-gradient-to-br from-brand-accent/20 to-brand-accent/40 rounded-lg mb-4 flex items-center justify-center border border-brand-accent/30">
              <span className="text-xl">📱</span>
            </div>
            <h3 className="text-lg font-bold text-brand-accent mb-2 font-serif-chinese">纸雕艺术</h3>
            <p className="text-neutral-300 text-sm leading-relaxed">
              测算结果一键跳转「纸喜爱纸艺」小程序，将守护星变为纸雕艺术品。
            </p>
          </Card>
        </section>

        {/* ===== 第四屏：玉皇庙彩塑艺术 ===== */}
        <section id="sculpture" className="max-w-4xl mx-auto mb-16 md:mb-20">
          <div className="text-center mb-8">
            <h2 className="text-2xl md:text-3xl font-bold text-brand-accent mb-4 font-serif-chinese">
              玉皇庙彩塑艺术
            </h2>
            <p className="text-neutral-300 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
              山西晋城玉皇庙，始建于隋宋，历经千年仍保留完整的二十八星宿彩塑群像。
              这些彩塑以神话传说中的动物形象为蓝本，结合五行八卦哲学，塑造出各具神态的星宿人物。
              每尊造像眉目传神、衣纹流畅，是中国古代泥塑艺术的瑰宝。
            </p>
          </div>
          
          {/* 塑像图片入口 */}
          <div 
            className="flex justify-center gap-4 cursor-pointer group"
            onClick={() => navigate('/culture')}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => e.key === 'Enter' && navigate('/culture')}
            aria-label="点击进入玉皇庙彩塑专题"
          >
            {['kangjinlong', 'xinyuehu', 'weihuohu', 'shenshuiyuan'].map((name, i) => (
              <img 
                key={i}
                src={`/images/constellations/sculpture/${name}_thumb.webp`}
                alt={`${name}彩塑`}
                className="w-[100px] h-[100px] object-cover rounded-lg group-hover:opacity-80 transition-opacity"
              />
            ))}
          </div>
          <p className="text-center text-neutral-400 text-sm mt-4">点击图片进入玉皇庙彩塑专题</p>
        </section>
      </div>

      {/* ===== 图片灯箱：支持手势缩放 + 左右滑动预览 ===== */}
      {lightboxImg && lightboxIndex !== null && (
        <div
          className="lightbox-backdrop"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          aria-label={`${lightboxImg.name}大图预览`}
        >
          <button
            className="lightbox-close"
            onClick={closeLightbox}
            aria-label="关闭大图预览"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* 左箭头 */}
          <button
            className="lightbox-nav lightbox-nav-prev"
            onClick={(e) => { e.stopPropagation(); goToPrev(); }}
            aria-label="上一张"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* 右箭头 */}
          <button
            className="lightbox-nav lightbox-nav-next"
            onClick={(e) => { e.stopPropagation(); goToNext(); }}
            aria-label="下一张"
          >
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* 星宿名称 + 序号 */}
          <div className="lightbox-title">
            {lightboxImg.name}
            <span className="lightbox-counter">{lightboxIndex + 1} / {lightboxImages.length}</span>
          </div>

          {/* 可缩放图片容器 — 跟手滑动切换 */}
          <div
            className="lightbox-zoom-container"
            onClick={(e) => e.stopPropagation()}
          >
            <GalleryZoomImage
              index={lightboxIndex}
              items={lightboxImages.map(img => ({ src: img.src, alt: img.name }))}
              onIndexChange={setLightboxIndex}
            />
          </div>

          {/* 缩放提示 */}
          <div className="lightbox-hint">左右滑动切换 · 双指或滚轮缩放</div>
        </div>
      )}

    </Layout>
  );
};

export default HomePage;
